import { EventWebsiteState, useUpdateEventWebsiteStateMutation } from '@graphql/generated';
import { useFeatureValue } from '@shared/core/featureFlags';
import { useEventFeatureFlags } from '@shared/hooks/useEventFeatureFlags/useEventFeatureFlags';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useCallback, useMemo } from 'react';

export const useWebsitePublishCapabilityFeature = (): Readonly<{
  eventExposure: Readonly<{
    value: boolean;
    isLoading: boolean;
  }>;
  eventPublishedState: Readonly<{
    value: boolean;
    update: (published: boolean) => Promise<unknown>;
    isLoading: boolean;
    isUpdating: boolean;
  }>;
}> => {
  const { eventHandle, eventInfo, loading } = useEventInfo();

  const eventFeatureFlags = useEventFeatureFlags({ eventHandle: eventHandle as string, skip: !eventHandle });
  const isEventEligible = !eventFeatureFlags.loadingEventFeatureFlags && eventFeatureFlags.isEventFeatureFlagEnabled('useWebsitePublishedState');

  const featureValue = useFeatureValue('websitePublishCapabilityEnabled', {
    skip: !isEventEligible,
  });

  const [mutate, { loading: isUpdatingEventWebsiteState }] = useUpdateEventWebsiteStateMutation();

  const updateEventWebsiteState = useCallback(
    async (published: boolean) => {
      if (!eventInfo?.eventId) {
        return;
      }

      return mutate({
        variables: {
          payload: {
            eventId: eventInfo.eventId,
            websiteState: published ? EventWebsiteState.published : EventWebsiteState.draft,
          },
        },
      });
    },
    [eventInfo, mutate],
  );

  const eventExposure = useMemo(
    () => ({
      value: featureValue.value === 'on',
      isLoading: featureValue.loading,
    }),
    [featureValue.loading, featureValue.value],
  );

  const eventPublishedState = useMemo(
    () => ({
      value: eventInfo?.websiteState === EventWebsiteState.published,
      update: updateEventWebsiteState,
      isLoading: loading,
      isUpdating: isUpdatingEventWebsiteState,
    }),
    [eventInfo?.websiteState, loading, updateEventWebsiteState, isUpdatingEventWebsiteState],
  );

  return {
    eventExposure,
    eventPublishedState,
  };
};
